/**
 * Slim, dismissible "Get the C1 Staffing app" bar for signed-in workers on
 * phones. Ships OFF — see src/utils/workerAppBanner.ts for the Firestore
 * switch, the QA preview param, and the show/hide rules.
 *
 * Web-only by nature (it advertises the native app), so there is no
 * c1_app counterpart; noted in the Flutter parity punch list.
 *
 * Step 6 (2026-09-11): on payroll pages, right after payroll setup finishes,
 * it renders a one-time "You're ready to work" prompt instead of the slim bar
 * (see isAppReadyMoment in src/utils/workerAppBanner.ts).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import CloseIcon from '@mui/icons-material/Close';

import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../i18n';
import {
  ANDROID_PACKAGE_ID,
  APP_BANNER_DISMISSED_EVENT,
  APP_BANNER_DISMISS_MS,
  APP_BANNER_PREVIEW_PARAM,
  APP_BANNER_PREVIEW_SESSION_KEY,
  APP_READY_PREVIEW_VALUE,
  WORKER_APP_BANNER_OFF,
  appBannerDismissKey,
  appReadyMomentKey,
  detectAppBannerPlatform,
  isAppReadyMoment,
  isPayrollPath,
  latestPayrollCompletionMs,
  resolveWorkerAppBanner,
  type WorkerAppBannerConfig,
} from '../../utils/workerAppBanner';
import { C1_TENANT_ID_CANONICAL } from '../../utils/c1TenantIdNormalize';
import {
  dismissAppBanner,
  loadWorkerAppBannerConfig,
  readAppBannerPreviewFlag,
} from '../../hooks/useWorkerAppBannerDecision';

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const loadBannerConfig = loadWorkerAppBannerConfig;
const readPreviewFlag = readAppBannerPreviewFlag;

type RelatedApp = { platform?: string; id?: string };
type NavigatorWithRelatedApps = Navigator & { getInstalledRelatedApps?: () => Promise<RelatedApp[]> };

const WorkerAppDownloadBanner: React.FC = () => {
  const t = useT();
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const uid = user?.uid;

  const [config, setConfig] = useState<WorkerAppBannerConfig>(WORKER_APP_BANNER_OFF);
  const [installed, setInstalled] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const preview = useMemo(() => readPreviewFlag(search), [search]);

  const platform = useMemo(
    () =>
      typeof navigator === 'undefined'
        ? null
        : detectAppBannerPlatform(navigator.userAgent || '', navigator.maxTouchPoints || 0),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!uid || (!platform && !preview)) return undefined;
    void loadBannerConfig().then((next) => {
      if (!cancelled) setConfig(next);
    });
    return () => {
      cancelled = true;
    };
  }, [uid, platform, preview]);

  useEffect(() => {
    if (!uid) return undefined;
    try {
      const raw = window.localStorage.getItem(appBannerDismissKey(uid));
      const until = raw ? Number(raw) : 0;
      setDismissedUntil(Number.isFinite(until) ? until : 0);
    } catch {
      setDismissedUntil(0);
    }
    // "Not now" on the Home action item hides this bar without a reload.
    const onDismissed = (e: Event) => {
      const detail = (e as CustomEvent<{ uid?: string; until?: number }>).detail;
      if (!detail || detail.uid === uid) setDismissedUntil(typeof detail?.until === 'number' ? detail.until : Date.now() + APP_BANNER_DISMISS_MS);
    };
    window.addEventListener(APP_BANNER_DISMISSED_EVENT, onDismissed);
    return () => window.removeEventListener(APP_BANNER_DISMISSED_EVENT, onDismissed);
  }, [uid]);

  // Best effort: Chrome on Android can report the installed app when it is
  // listed in manifest.json related_applications. Anything else → not installed.
  useEffect(() => {
    let cancelled = false;
    const nav = typeof navigator === 'undefined' ? undefined : (navigator as NavigatorWithRelatedApps);
    if (platform !== 'android' || !nav?.getInstalledRelatedApps) return undefined;
    nav
      .getInstalledRelatedApps()
      .then((apps) => {
        if (!cancelled) setInstalled(apps.some((app) => app.id === ANDROID_PACKAGE_ID));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [platform]);

  // Step 6 (2026-09-11): the "ready to work" moment — right after payroll setup
  // finishes on the web, payroll pages show a one-time fuller prompt instead of
  // the slim bar (ignores the 30-day dismissal; never repeats once seen).
  const readyPreview = useMemo(
    () => new URLSearchParams(search).get(APP_BANNER_PREVIEW_PARAM) === APP_READY_PREVIEW_VALUE,
    [search],
  );
  const [momentSeen, setMomentSeen] = useState(true);
  const [completedAtMs, setCompletedAtMs] = useState<number | null>(null);
  const [hasAppPushToken, setHasAppPushToken] = useState(true);
  const momentCandidate = Boolean(
    uid &&
      isPayrollPath(pathname) &&
      resolveWorkerAppBanner({ platform, config, pathname, dismissedUntil: 0, now: Date.now(), installed, preview }),
  );

  useEffect(() => {
    if (!uid) return;
    try {
      setMomentSeen(window.localStorage.getItem(appReadyMomentKey(uid)) === '1');
    } catch {
      setMomentSeen(false);
    }
  }, [uid]);

  // Only read employments / push tokens when the moment could actually show.
  useEffect(() => {
    if (!uid || !momentCandidate || momentSeen) return undefined;
    let cancelled = false;
    void getDocs(
      query(collection(db, 'users', uid, 'pushTokens'), where('platform', 'in', ['iOS', 'Android', 'ios', 'android']), limit(1)),
    )
      .then((snap) => {
        if (!cancelled) setHasAppPushToken(!snap.empty);
      })
      .catch(() => {
        if (!cancelled) setHasAppPushToken(false);
      });
    const unsub = onSnapshot(
      query(collection(db, 'tenants', C1_TENANT_ID_CANONICAL, 'entity_employments'), where('userId', '==', uid)),
      (snap) => setCompletedAtMs(latestPayrollCompletionMs(snap.docs.map((d) => d.data()))),
      () => setCompletedAtMs(null),
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid, momentCandidate, momentSeen]);

  if (!uid) return null;

  const now = Date.now();
  const momentBanner = resolveWorkerAppBanner({
    platform,
    config,
    pathname,
    dismissedUntil: 0,
    now,
    installed,
    preview: preview || readyPreview,
  });
  const showMoment =
    Boolean(momentBanner) &&
    isPayrollPath(pathname) &&
    (readyPreview ||
      isAppReadyMoment({ banner: momentBanner, pathname, completedAtMs, now, seen: momentSeen, hasAppPushToken }));
  if (showMoment && momentBanner) {
    const markSeen = () => {
      if (!readyPreview) {
        try {
          window.localStorage.setItem(appReadyMomentKey(uid), '1');
        } catch {
          // Storage blocked: hide for this page view only.
        }
      }
      setMomentSeen(true);
    };
    return (
      <aside
        aria-label={t('appBanner.readyTitle')}
        data-testid="worker-app-ready-moment"
        style={{
          padding: '14px 14px 12px',
          marginBottom: 16,
          background: '#fff',
          border: '1px solid #e6e6e3',
          borderRadius: 12,
          fontFamily: FONT,
          color: '#111',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{t('appBanner.readyTitle')}</div>
        <div style={{ fontSize: 14, color: '#6b6b66', lineHeight: 1.4, marginTop: 4 }}>{t('appBanner.readyBody')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <a
            href={momentBanner.storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={markSeen}
            style={{
              background: '#111',
              color: '#fff',
              borderRadius: 999,
              padding: '8px 14px',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {t('appBanner.cta')}
          </a>
          <button
            type="button"
            onClick={markSeen}
            style={{ border: 0, background: 'transparent', color: '#6b6b66', fontSize: 14, cursor: 'pointer', padding: 6 }}
          >
            {t('appBanner.dismiss')}
          </button>
        </div>
      </aside>
    );
  }

  const decision = resolveWorkerAppBanner({
    platform,
    config,
    pathname,
    dismissedUntil,
    now: Date.now(),
    installed,
    preview,
  });
  if (!decision) return null;

  const dismiss = () => {
    // Shared with the Home action item (useWorkerAppBannerDecision): 30 days + an event so the other surface hides too.
    const until = dismissAppBanner(uid);
    if (preview) {
      try {
        window.sessionStorage.removeItem(APP_BANNER_PREVIEW_SESSION_KEY);
      } catch {
        // ignore
      }
    }
    setDismissedUntil(until);
    if (preview) window.location.replace(pathname);
  };

  return (
    <aside
      aria-label={t('appBanner.title')}
      data-testid="worker-app-download-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 4px 8px 14px',
        marginBottom: 16,
        background: '#fff',
        border: '1px solid #e6e6e3',
        borderRadius: 12,
        fontFamily: FONT,
        color: '#111',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{t('appBanner.title')}</div>
        <div style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.35 }}>{t('appBanner.body')}</div>
      </div>
      <a
        href={decision.storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          flexShrink: 0,
          background: '#111',
          color: '#fff',
          borderRadius: 999,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {t('appBanner.cta')}
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('appBanner.dismiss')}
        style={{
          flexShrink: 0,
          border: 0,
          background: 'transparent',
          color: '#6b6b66',
          padding: 6,
          cursor: 'pointer',
          display: 'flex',
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </button>
    </aside>
  );
};

export default WorkerAppDownloadBanner;
